import { Tab, Tabs } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { USER_ROLES } from '@/constants/userRoles'
import { useAuth } from '@/features/authentication/contexts/AuthContext'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import { isResourceConfigurationTabVisible } from '@/features/resource/configuration/utils/resourceConfigurationVisibility'
import { isSoCManagementTabVisible } from '@/features/resource/configuration/utils/socManagementVisibility'
import { ResourceRouteInformation } from '@/features/resource/constants'
import { useResourceData } from '@/features/resource/contexts/ResourceDataContext'

const commonProps = {
  component: Link,
  disableRipple: true,
  iconPosition: 'start',
  style: { minHeight: 0 },
} as const

type LocalResourceTabsProps = Readonly<{
  activeTab: string
}>

/**
 * Tab strip for any LOCAL-control resource (`ResourceTabs` dispatches on `controlMode` alone): the
 * General overview (activation groups and ports; edits there are limited to measure / metering
 * relation), the consolidated config tab (LC-191 form + LC-94 binding panel), Custom attributes, and
 * the admin-only Support toolbox. Only the config tab is battery-gated, as below.
 *
 * General is first and is the tab LOCAL resources land on (see `ResourceDetailsIndex`). It carries
 * the activation-groups overview — including the read-only Activatable column, which matters for
 * SYMPOWER_LOCAL mFRR — and is not role-gated, because `/general` is mounted unconditionally.
 *
 * The standalone "Local control" tab was retired in favour of the single editable
 * Resource configuration tab, so the resource has exactly one editable *configuration* surface; the
 * stale `/local-control` URL now redirects to `/general` for everyone (LC-405).
 *
 * The Resource configuration tab is gated on `isResourceConfigurationTabVisible` (admin/trader +
 * LOCAL battery), while its route is mounted on the role-only subset
 * (`canUserReachResourceConfigurationRoute`). The tab predicate is therefore a strict subset of the
 * mount predicate, so the tab can never 404 on click.
 */
export function LocalResourceTabs({ activeTab }: LocalResourceTabsProps) {
  const { resourceUuid } = useParams()
  const { t } = useTranslation()
  const { loggedInUser } = useAuth()
  const { resource } = useResourceData()
  const { permissions } = usePermissions()

  const showConfigurationTab = isResourceConfigurationTabVisible({
    resource,
    loggedInUser,
  })

  const showSoCManagementTab = isSoCManagementTabVisible({
    resource,
    hasTargetSocPermission: permissions.has('targetSocMutation'),
  })

  // The tabs only mount under the `/resources/:resourceUuid/*` route tree, so the param is in
  // practice always present — but TypeScript can't see that, and `useParams()` returns
  // `string | undefined`. Bail out instead of asserting non-null.
  if (!resourceUuid) return null

  return (
    <Tabs aria-label="resource-tabs" value={activeTab}>
      <Tab
        {...commonProps}
        label={t('resources.tabs.general')}
        to={ResourceRouteInformation.RESOURCE_GENERAL.navigationPath(resourceUuid)}
        value={ResourceRouteInformation.RESOURCE_GENERAL.routePath}
      />
      {showConfigurationTab && (
        <Tab
          {...commonProps}
          label={t('resources.tabs.resource_configuration')}
          to={ResourceRouteInformation.RESOURCE_CONFIGURATION.navigationPath(resourceUuid)}
          value={ResourceRouteInformation.RESOURCE_CONFIGURATION.routePath}
        />
      )}
      <Tab
        {...commonProps}
        label={t('resources.tabs.custom_attributes')}
        to={ResourceRouteInformation.RESOURCE_CUSTOM_ATTRIBUTES.navigationPath(resourceUuid)}
        value={ResourceRouteInformation.RESOURCE_CUSTOM_ATTRIBUTES.routePath}
      />
      {showSoCManagementTab && (
        <Tab
          {...commonProps}
          label={t('resources.tabs.soc_management')}
          to={ResourceRouteInformation.RESOURCE_SOC_MANAGEMENT.navigationPath(resourceUuid)}
          value={ResourceRouteInformation.RESOURCE_SOC_MANAGEMENT.routePath}
        />
      )}
      {loggedInUser?.role === USER_ROLES.ADMINISTRATORS.value && (
        <Tab
          {...commonProps}
          label={t('resources.tabs.support_toolbox')}
          to={ResourceRouteInformation.RESOURCE_SUPPORT_TOOLBOX.navigationPath(resourceUuid)}
          value={ResourceRouteInformation.RESOURCE_SUPPORT_TOOLBOX.routePath}
        />
      )}
    </Tabs>
  )
}
